"use client"

import Header from "@/components/header"
import Footer from "@/components/footer"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Progress } from "@/components/ui/progress"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import {
    ArrowUpRight,
    ArrowDownRight,
    ChevronRight,
    CheckCircle2,
    XCircle,
    Loader2,
} from "lucide-react"
import { Coins, TrendingUp, Zap, Users, Gift, Wallet, Star, Clock, Lock, Flame, Eye } from "lucide-react"
import Link from "next/link"
import { useState } from "react"
import { useWallet } from "@/lib/wallet-context"

// Staking pools with duration-based APY tiers
const stakingPools = [
    {
        id: "pool-1",
        name: "TKS Staking",
        symbol: "TKS",
        poolType: "TKS",
        logo: null,
        durations: [
            { days: 30, apy: 100 },
            { days: 60, apy: 150 },
        ],
        tvl: 2500000,
        totalStaked: 15000000,
        yourStake: 5000,
        yourDuration: 30,
        pendingRewards: 125.5,
        minStake: 100,
        maxStake: 100000,
        featured: true,
        status: "active",
        network: "BSC",
        rewardToken: "TKS",
        depositFee: 0,
        withdrawFee: 0,
        participants: 1234,
        isNative: false,
    },
    {
        id: "pool-2",
        name: "TKS-BNB LP",
        symbol: "TKS-BNB",
        poolType: "LP",
        logo: null,
        durations: [
            { days: 30, apy: 200 },
            { days: 60, apy: 300 },
        ],
        tvl: 1800000,
        totalStaked: 8500000,
        yourStake: 0,
        yourDuration: 0,
        pendingRewards: 0,
        minStake: 0.1,
        maxStake: 10000,
        featured: true,
        status: "active",
        network: "BSC",
        rewardToken: "TKS",
        depositFee: 0,
        withdrawFee: 0.1,
        participants: 856,
        isNative: false,
    },
    {
        id: "pool-3",
        name: "USDT Vault",
        symbol: "USDT",
        poolType: "USDT",
        logo: null,
        durations: [
            { days: 1, apy: 20 },
            { days: 7, apy: 35 },
            { days: 30, apy: 50 },
            { days: 60, apy: 65 },
            { days: 365, apy: 80 },
        ],
        tvl: 5000000,
        totalStaked: 5000000,
        yourStake: 1000,
        yourDuration: 30,
        pendingRewards: 12.3,
        minStake: 10,
        maxStake: 500000,
        featured: false,
        status: "active",
        network: "BSC",
        rewardToken: "TKS",
        depositFee: 0,
        withdrawFee: 0,
        participants: 2341,
        isNative: false,
    },
    {
        id: "pool-4",
        name: "BNB Flexible",
        symbol: "BNB",
        poolType: "BNB",
        logo: null,
        durations: [
            { days: 1, apy: 15 },
            { days: 7, apy: 25 },
            { days: 30, apy: 40 },
            { days: 60, apy: 50 },
            { days: 365, apy: 60 },
        ],
        tvl: 3200000,
        totalStaked: 12000,
        yourStake: 0,
        yourDuration: 0,
        pendingRewards: 0,
        minStake: 0.01,
        maxStake: 1000,
        featured: false,
        status: "active",
        network: "BSC",
        rewardToken: "TKS",
        depositFee: 0,
        withdrawFee: 0,
        participants: 1567,
        isNative: true,
    },
]

// User positions
const userPositions = stakingPools.filter(p => p.yourStake > 0)

export function StakingContent() {
    const { isConnected, connectWallet, address } = useWallet()
    const [activeTab, setActiveTab] = useState("all")
    const [showStakeModal, setShowStakeModal] = useState(false)
    const [showUnstakeModal, setShowUnstakeModal] = useState(false)
    const [showClaimModal, setShowClaimModal] = useState(false)
    const [selectedPool, setSelectedPool] = useState<typeof stakingPools[0] | null>(null)
    const [stakeAmount, setStakeAmount] = useState("")
    const [unstakeAmount, setUnstakeAmount] = useState("")

    // Transaction states: 'idle' | 'pending' | 'success' | 'error'
    const [txState, setTxState] = useState<'idle' | 'pending' | 'success' | 'error'>('idle')
    const [txHash, setTxHash] = useState("")
    const [selectedDuration, setSelectedDuration] = useState<number>(0)

    const totalStakedValue = userPositions.reduce((sum, p) => sum + p.yourStake, 0)
    const totalPendingRewards = userPositions.reduce((sum, p) => sum + p.pendingRewards, 0)

    const handleStake = (pool: typeof stakingPools[0]) => {
        setSelectedPool(pool)
        setStakeAmount("")
        setSelectedDuration(pool.durations[0].days) // Default to first duration
        setTxState('idle')
        setShowStakeModal(true)
    }

    const handleUnstake = (pool: typeof stakingPools[0]) => {
        setSelectedPool(pool)
        setUnstakeAmount("")
        setTxState('idle')
        setShowUnstakeModal(true)
    }

    const handleClaim = (pool: typeof stakingPools[0]) => {
        setSelectedPool(pool)
        setTxState('idle')
        setShowClaimModal(true)
    }

    const executeStake = async () => {
        if (!stakeAmount || parseFloat(stakeAmount) <= 0) return
        setTxState('pending')

        // Simulate transaction - randomly succeed or fail
        await new Promise(resolve => setTimeout(resolve, 2000))

        const success = Math.random() > 0.3 // 70% success rate for demo
        if (success) {
            setTxHash("0x" + Math.random().toString(16).slice(2, 66))
            setTxState('success')
        } else {
            setTxState('error')
        }
    }

    const executeUnstake = async () => {
        if (!unstakeAmount || parseFloat(unstakeAmount) <= 0) return
        setTxState('pending')

        await new Promise(resolve => setTimeout(resolve, 2000))

        const success = Math.random() > 0.3
        if (success) {
            setTxHash("0x" + Math.random().toString(16).slice(2, 66))
            setTxState('success')
        } else {
            setTxState('error')
        }
    }

    const executeClaim = async () => {
        setTxState('pending')

        await new Promise(resolve => setTimeout(resolve, 2000))

        const success = Math.random() > 0.3
        if (success) {
            setTxHash("0x" + Math.random().toString(16).slice(2, 66))
            setTxState('success')
        } else {
            setTxState('error')
        }
    }

    const resetAndClose = (modalType: 'stake' | 'unstake' | 'claim') => {
        setTxState('idle')
        setTxHash("")
        if (modalType === 'stake') {
            setShowStakeModal(false)
        } else if (modalType === 'unstake') {
            setShowUnstakeModal(false)
        } else {
            setShowClaimModal(false)
        }
    }

    const formatNumber = (num: number) => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`
        if (num >= 1000) return `${(num / 1000).toFixed(2)}K`
        return num.toFixed(2)
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1 pt-24 pb-12 md:pt-28 px-4">
                <div className="container">
                    {/* Page Header */}
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-2">
                            <Coins className="h-10 w-10 text-primary" />
                            <h1 className="text-3xl font-bold tracking-tight">Staking</h1>
                        </div>
                        <p className="text-muted-foreground">
                            Stake your tokens to earn rewards. Higher APY for longer lock periods.
                        </p>
                    </div>

                    {/* Stats Overview */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                                        <TrendingUp className="h-7 w-7 text-primary" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-2xl font-bold truncate">$12.5M</p>
                                        <p className="text-xs text-muted-foreground">Total Value Locked</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-lg bg-[#7ed957]/10 flex items-center justify-center">
                                        <Zap className="h-7 w-7 text-[#7ed957]" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-2xl font-bold truncate">250%</p>
                                        <p className="text-xs text-muted-foreground">Max APY</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                        <Users className="h-7 w-7 text-blue-500" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-2xl font-bold truncate">5,998</p>
                                        <p className="text-xs text-muted-foreground">Total Stakers</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                                        <Gift className="h-7 w-7 text-purple-500" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">$2.3M</p>
                                        <p className="text-xs text-muted-foreground">Total Rewards Paid</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Your Positions (if connected and has stakes) */}
                    {isConnected && userPositions.length > 0 && (
                        <Card className="mb-8 border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Wallet className="h-7 w-7 text-primary" />
                                    Your Staking Positions
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid md:grid-cols-3 gap-4 mb-6">
                                    <div className="rounded-lg border bg-background p-4 text-center">
                                        <div className="text-2xl font-bold">${formatNumber(totalStakedValue)}</div>
                                        <div className="text-sm text-muted-foreground">Total Staked</div>
                                    </div>
                                    <div className="rounded-lg border bg-background p-4 text-center">
                                        <div className="text-2xl font-bold text-[#7ed957]">{totalPendingRewards.toFixed(2)} TKS</div>
                                        <div className="text-sm text-muted-foreground">Pending Rewards</div>
                                    </div>
                                    <div className="rounded-lg border bg-background p-4 text-center">
                                        <div className="text-2xl font-bold">{userPositions.length}</div>
                                        <div className="text-sm text-muted-foreground">Active Positions</div>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    {userPositions.map((pool) => (
                                        <div key={pool.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg border bg-background">
                                            <div className="flex items-center gap-3">
                                                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                                                    <Coins className="h-6 w-6 text-primary" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="font-medium">{pool.name}</div>
                                                    <div className="text-sm text-muted-foreground">
                                                        Staked: {pool.yourStake.toLocaleString()} {pool.symbol}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between sm:justify-end gap-4">
                                                <div className="text-left sm:text-right">
                                                    <div className="font-medium text-[#7ed957]">+{pool.pendingRewards.toFixed(2)} {pool.rewardToken}</div>
                                                    <div className="text-sm text-muted-foreground">{pool.durations.find(d => d.days === pool.yourDuration)?.apy || pool.durations[0].apy}% APY</div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button size="sm" variant="outline" onClick={() => handleUnstake(pool)}>
                                                        <ArrowDownRight className="h-4 w-4 mr-1" />
                                                        Unstake
                                                    </Button>
                                                    <Button size="sm" onClick={() => handleClaim(pool)}>
                                                        <Gift className="h-4 w-4 mr-1 text-primary" />
                                                        Claim
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-4 flex justify-center">
                                    <Button variant="outline">
                                        Claim All Rewards ({totalPendingRewards.toFixed(2)} TKS)
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Pool Categories */}
                    <Tabs defaultValue="all" className="mb-6" onValueChange={setActiveTab}>
                        <TabsList className="flex-wrap h-auto gap-1">
                            <TabsTrigger value="all">All Pools</TabsTrigger>
                            <TabsTrigger value="featured" className="gap-2">
                                <Star className="h-4 w-4" />
                                Featured
                            </TabsTrigger>
                            <TabsTrigger value="flexible" className="gap-2">
                                <Clock className="h-4 w-4" />
                                Flexible
                            </TabsTrigger>
                            <TabsTrigger value="locked" className="gap-2">
                                <Lock className="h-4 w-4" />
                                Locked
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {/* Staking Pools Grid */}
                    <div className="grid md:grid-cols-2 gap-6">
                        {stakingPools
                            .filter(pool => {
                                if (activeTab === "featured") return pool.featured
                                // Flexible = has 1-day option (USDT, BNB)
                                if (activeTab === "flexible") return pool.durations.some(d => d.days <= 7)
                                // Locked = minimum 30 days (TKS, LP)
                                if (activeTab === "locked") return pool.durations.every(d => d.days >= 30)
                                return true
                            })
                            .map((pool) => (
                                <Card key={pool.id} className={pool.featured ? "border-primary/50 relative overflow-hidden" : ""}>
                                    {pool.featured && (
                                        <div className="absolute top-0 right-0">
                                            <Badge className="rounded-none rounded-bl-lg bg-primary">
                                                <Flame className="h-3 w-3 mr-1" />
                                                Hot
                                            </Badge>
                                        </div>
                                    )}
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center">
                                                    <Coins className="h-10 w-10 text-primary" />
                                                </div>
                                                <div>
                                                    <CardTitle className="text-lg">{pool.name}</CardTitle>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <Badge variant="secondary">{pool.network}</Badge>
                                                        {pool.durations.every(d => d.days >= 30) ? (
                                                            <Badge variant="outline" className="gap-1">
                                                                <Lock className="h-3 w-3" />
                                                                {pool.durations[0].days}-{pool.durations[pool.durations.length - 1].days}d
                                                            </Badge>
                                                        ) : (
                                                            <Badge variant="outline" className="text-[#7ed957] border-[#7ed957]/50">
                                                                Flexible
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-2xl font-bold text-[#7ed957]">
                                                    {pool.durations[0].apy}-{pool.durations[pool.durations.length - 1].apy}%
                                                </div>
                                                <div className="text-xs text-muted-foreground">APY Range</div>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <div className="text-sm text-muted-foreground">TVL</div>
                                                <div className="font-bold">${formatNumber(pool.tvl)}</div>
                                            </div>
                                            <div>
                                                <div className="text-sm text-muted-foreground">Stakers</div>
                                                <div className="font-bold">{pool.participants.toLocaleString()}</div>
                                            </div>
                                            <div>
                                                <div className="text-sm text-muted-foreground">Earn</div>
                                                <div className="font-bold">{pool.rewardToken}</div>
                                            </div>
                                            <div>
                                                <div className="text-sm text-muted-foreground">Deposit Fee</div>
                                                <div className="font-bold">{pool.depositFee}%</div>
                                            </div>
                                        </div>

                                        {pool.yourStake > 0 && (
                                            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-muted-foreground">Your Stake</span>
                                                    <span className="font-medium">{pool.yourStake.toLocaleString()} {pool.symbol}</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-muted-foreground">Pending Rewards</span>
                                                    <span className="font-medium text-[#7ed957]">+{pool.pendingRewards.toFixed(2)} {pool.rewardToken}</span>
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex gap-2">
                                            {isConnected ? (
                                                <>
                                                    <Button className="flex-1" onClick={() => handleStake(pool)}>
                                                        <ArrowUpRight className="h-4 w-4 mr-2" />
                                                        Stake
                                                    </Button>
                                                    {pool.yourStake > 0 && (
                                                        <Button variant="outline" className="flex-1" onClick={() => handleUnstake(pool)}>
                                                            <ArrowDownRight className="h-4 w-4 mr-2" />
                                                            Unstake
                                                        </Button>
                                                    )}
                                                </>
                                            ) : (
                                                <Button className="w-full" onClick={connectWallet}>
                                                    <Wallet className="h-4 w-4 mr-2" />
                                                    Connect Wallet
                                                </Button>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                    </div>

                    {/* Info Section */}
                    <Card className="mt-8">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Eye className="h-7 w-7 text-primary" />
                                How Staking Works
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                <div className="text-center">
                                    <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                                        <Wallet className="h-10 w-10 text-primary" />
                                    </div>
                                    <h4 className="font-medium mb-1">1. Connect Wallet</h4>
                                    <p className="text-sm text-muted-foreground">Connect your wallet to access staking pools</p>
                                </div>
                                <div className="text-center">
                                    <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                                        <Coins className="h-10 w-10 text-primary" />
                                    </div>
                                    <h4 className="font-medium mb-1">2. Choose a Pool</h4>
                                    <p className="text-sm text-muted-foreground">Select a pool based on APY and lock period</p>
                                </div>
                                <div className="text-center">
                                    <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                                        <Lock className="h-10 w-10 text-primary" />
                                    </div>
                                    <h4 className="font-medium mb-1">3. Stake Tokens</h4>
                                    <p className="text-sm text-muted-foreground">Stake your tokens and start earning rewards</p>
                                </div>
                                <div className="text-center">
                                    <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                                        <Gift className="h-10 w-10 text-purple-500" />
                                    </div>
                                    <h4 className="font-medium mb-1">4. Claim Rewards</h4>
                                    <p className="text-sm text-muted-foreground">Claim your earned rewards anytime</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>

            {/* Stake Modal */}
            <Dialog open={showStakeModal} onOpenChange={(open) => !open && resetAndClose('stake')}>
                <DialogContent>
                    {txState === 'idle' && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <ArrowUpRight className="h-5 w-5 text-primary" />
                                    Stake {selectedPool?.symbol}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                                            <Coins className="h-6 w-6 text-primary" />
                                        </div>
                                        <div>
                                            <div className="font-medium">{selectedPool?.name}</div>
                                            <div className="text-sm text-muted-foreground">
                                                {selectedPool?.durations.find(d => d.days === selectedDuration)?.apy || 0}% APY
                                            </div>
                                        </div>
                                    </div>
                                    <Badge variant="outline">
                                        <Lock className="h-3 w-3 mr-1" />
                                        {selectedDuration} days
                                    </Badge>
                                </div>

                                {/* Duration Selector */}
                                <div className="space-y-2">
                                    <Label>Lock Duration</Label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {selectedPool?.durations.map((tier) => (
                                            <Button
                                                key={tier.days}
                                                variant={selectedDuration === tier.days ? "default" : "outline"}
                                                className="flex flex-col h-auto py-3"
                                                onClick={() => setSelectedDuration(tier.days)}
                                            >
                                                <span className="font-bold">{tier.days}d</span>
                                                <span className="text-xs text-[#7ed957]">{tier.apy}% APY</span>
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <Label>Amount to Stake</Label>
                                        <span className="text-sm text-muted-foreground">Balance: 10,000 {selectedPool?.symbol}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <Input
                                            type="number"
                                            placeholder="0.00"
                                            value={stakeAmount}
                                            onChange={(e) => setStakeAmount(e.target.value)}
                                        />
                                        <Button variant="outline" onClick={() => setStakeAmount("10000")}>MAX</Button>
                                    </div>
                                </div>

                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Estimated Daily Rewards</span>
                                        <span className="font-medium">
                                            ~{stakeAmount ? (parseFloat(stakeAmount) * (selectedPool?.durations.find(d => d.days === selectedDuration)?.apy || 0) / 100 / 365).toFixed(4) : "0"} {selectedPool?.rewardToken}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Deposit Fee</span>
                                        <span className="font-medium">{selectedPool?.depositFee}%</span>
                                    </div>
                                </div>

                                <div className="flex gap-2 p-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5">
                                    <Lock className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-muted-foreground">
                                        Your tokens will be locked for {selectedDuration} days. You cannot withdraw during this period.
                                    </p>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => resetAndClose('stake')}>Cancel</Button>
                                <Button onClick={executeStake} disabled={!stakeAmount || parseFloat(stakeAmount) <= 0}>
                                    Stake {selectedPool?.symbol}
                                </Button>
                            </DialogFooter>
                        </>
                    )}

                    {txState === 'pending' && (
                        <div className="py-12 text-center">
                            <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-4" />
                            <h3 className="text-lg font-semibold mb-2">Processing Transaction</h3>
                            <p className="text-sm text-muted-foreground">
                                Staking {stakeAmount} {selectedPool?.symbol}...
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">Please confirm in your wallet</p>
                        </div>
                    )}

                    {txState === 'success' && (
                        <div className="py-8 text-center">
                            <div className="h-16 w-16 rounded-full bg-[#7ed957]/15 dark:bg-[#7ed957]/20 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="h-10 w-10 text-[#6bc448]" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2 text-[#6bc448]">Staking Successful!</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                You have successfully staked {stakeAmount} {selectedPool?.symbol}
                            </p>
                            <div className="p-3 rounded-lg bg-muted/50 mb-4">
                                <p className="text-xs text-muted-foreground mb-1">Transaction Hash</p>
                                <p className="font-mono text-xs truncate">{txHash}</p>
                            </div>
                            <Button onClick={() => resetAndClose('stake')} className="w-full">
                                Done
                            </Button>
                        </div>
                    )}

                    {txState === 'error' && (
                        <div className="py-8 text-center">
                            <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                                <XCircle className="h-10 w-10 text-red-600" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2 text-red-600">Transaction Failed</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Something went wrong while staking. Please try again.
                            </p>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => resetAndClose('stake')} className="flex-1">
                                    Cancel
                                </Button>
                                <Button onClick={() => setTxState('idle')} className="flex-1">
                                    Try Again
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Unstake Modal */}
            <Dialog open={showUnstakeModal} onOpenChange={(open) => !open && resetAndClose('unstake')}>
                <DialogContent>
                    {txState === 'idle' && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <ArrowDownRight className="h-5 w-5 text-orange-500" />
                                    Unstake {selectedPool?.symbol}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                                            <Coins className="h-6 w-6 text-primary" />
                                        </div>
                                        <div>
                                            <div className="font-medium">{selectedPool?.name}</div>
                                            <div className="text-sm text-muted-foreground">
                                                Staked: {selectedPool?.yourStake.toLocaleString()} {selectedPool?.symbol}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <Label>Amount to Unstake</Label>
                                        <span className="text-sm text-muted-foreground">
                                            Staked: {selectedPool?.yourStake.toLocaleString()} {selectedPool?.symbol}
                                        </span>
                                    </div>
                                    <div className="flex gap-2">
                                        <Input
                                            type="number"
                                            placeholder="0.00"
                                            value={unstakeAmount}
                                            onChange={(e) => setUnstakeAmount(e.target.value)}
                                        />
                                        <Button variant="outline" onClick={() => setUnstakeAmount(selectedPool?.yourStake.toString() || "0")}>MAX</Button>
                                    </div>
                                </div>

                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Pending Rewards</span>
                                        <span className="font-medium text-[#7ed957]">+{selectedPool?.pendingRewards.toFixed(2)} {selectedPool?.rewardToken}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Withdrawal Fee</span>
                                        <span className="font-medium">{selectedPool?.withdrawFee}%</span>
                                    </div>
                                </div>

                                <div className="p-3 rounded-lg border bg-[#7ed957]/5 border-[#7ed957]/30">
                                    <p className="text-xs text-muted-foreground">
                                        ✓ Your pending rewards ({selectedPool?.pendingRewards.toFixed(2)} {selectedPool?.rewardToken}) will be automatically claimed when you unstake.
                                    </p>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => resetAndClose('unstake')}>Cancel</Button>
                                <Button variant="destructive" onClick={executeUnstake} disabled={!unstakeAmount || parseFloat(unstakeAmount) <= 0}>
                                    Unstake & Claim
                                </Button>
                            </DialogFooter>
                        </>
                    )}

                    {txState === 'pending' && (
                        <div className="py-12 text-center">
                            <Loader2 className="h-16 w-16 animate-spin text-orange-500 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold mb-2">Processing Transaction</h3>
                            <p className="text-sm text-muted-foreground">
                                Unstaking {unstakeAmount} {selectedPool?.symbol}...
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">Please confirm in your wallet</p>
                        </div>
                    )}

                    {txState === 'success' && (
                        <div className="py-8 text-center">
                            <div className="h-16 w-16 rounded-full bg-[#7ed957]/15 dark:bg-[#7ed957]/20 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="h-10 w-10 text-[#6bc448]" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2 text-[#6bc448]">Unstake Successful!</h3>
                            <p className="text-sm text-muted-foreground mb-2">
                                You have successfully unstaked {unstakeAmount} {selectedPool?.symbol}
                            </p>
                            <p className="text-sm text-[#7ed957] mb-4">
                                +{selectedPool?.pendingRewards.toFixed(2)} {selectedPool?.rewardToken} rewards claimed!
                            </p>
                            <div className="p-3 rounded-lg bg-muted/50 mb-4">
                                <p className="text-xs text-muted-foreground mb-1">Transaction Hash</p>
                                <p className="font-mono text-xs truncate">{txHash}</p>
                            </div>
                            <Button onClick={() => resetAndClose('unstake')} className="w-full">
                                Done
                            </Button>
                        </div>
                    )}

                    {txState === 'error' && (
                        <div className="py-8 text-center">
                            <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                                <XCircle className="h-10 w-10 text-red-600" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2 text-red-600">Transaction Failed</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Something went wrong while unstaking. Please try again.
                            </p>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => resetAndClose('unstake')} className="flex-1">
                                    Cancel
                                </Button>
                                <Button onClick={() => setTxState('idle')} className="flex-1">
                                    Try Again
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Claim Modal */}
            <Dialog open={showClaimModal} onOpenChange={(open) => !open && resetAndClose('claim')}>
                <DialogContent>
                    {txState === 'idle' && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Gift className="h-7 w-7 text-purple-500" />
                                    Claim Rewards
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                                            <Coins className="h-6 w-6 text-primary" />
                                        </div>
                                        <div>
                                            <div className="font-medium">{selectedPool?.name}</div>
                                            <div className="text-sm text-muted-foreground">{selectedPool?.durations[0]?.apy || 0}% APY</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded-lg border border-[#7ed957]/30 bg-[#7ed957]/5 p-4 text-center">
                                    <p className="text-sm text-muted-foreground mb-2">Pending Rewards</p>
                                    <p className="text-3xl font-bold text-[#7ed957]">+{selectedPool?.pendingRewards.toFixed(2)} {selectedPool?.rewardToken}</p>
                                </div>

                                <div className="p-3 rounded-lg border bg-muted/50">
                                    <p className="text-xs text-muted-foreground">
                                        Claiming rewards will transfer your earned tokens to your wallet. Your staked tokens will remain in the pool.
                                    </p>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => resetAndClose('claim')}>Cancel</Button>
                                <Button onClick={executeClaim} className="bg-[#6bc448] hover:bg-[#6bc448]">
                                    <Gift className="h-4 w-4 mr-2 text-primary" />
                                    Claim {selectedPool?.pendingRewards.toFixed(2)} {selectedPool?.rewardToken}
                                </Button>
                            </DialogFooter>
                        </>
                    )}

                    {txState === 'pending' && (
                        <div className="py-12 text-center">
                            <Loader2 className="h-16 w-16 animate-spin text-[#7ed957] mx-auto mb-4" />
                            <h3 className="text-lg font-semibold mb-2">Processing Claim</h3>
                            <p className="text-sm text-muted-foreground">
                                Claiming {selectedPool?.pendingRewards.toFixed(2)} {selectedPool?.rewardToken}...
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">Please confirm in your wallet</p>
                        </div>
                    )}

                    {txState === 'success' && (
                        <div className="py-8 text-center">
                            <div className="h-16 w-16 rounded-full bg-[#7ed957]/15 dark:bg-[#7ed957]/20 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="h-10 w-10 text-[#6bc448]" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2 text-[#6bc448]">Rewards Claimed!</h3>
                            <p className="text-2xl font-bold text-[#7ed957] mb-2">
                                +{selectedPool?.pendingRewards.toFixed(2)} {selectedPool?.rewardToken}
                            </p>
                            <p className="text-sm text-muted-foreground mb-4">
                                Successfully transferred to your wallet
                            </p>
                            <div className="p-3 rounded-lg bg-muted/50 mb-4">
                                <p className="text-xs text-muted-foreground mb-1">Transaction Hash</p>
                                <p className="font-mono text-xs truncate">{txHash}</p>
                            </div>
                            <Button onClick={() => resetAndClose('claim')} className="w-full">
                                Done
                            </Button>
                        </div>
                    )}

                    {txState === 'error' && (
                        <div className="py-8 text-center">
                            <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                                <XCircle className="h-10 w-10 text-red-600" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2 text-red-600">Claim Failed</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Something went wrong while claiming rewards. Please try again.
                            </p>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => resetAndClose('claim')} className="flex-1">
                                    Cancel
                                </Button>
                                <Button onClick={() => setTxState('idle')} className="flex-1">
                                    Try Again
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* SEO explainer — below the content, above footer */}
            <section className="container max-w-3xl mx-auto pb-16">
                <div className="rounded-xl bg-muted/30 p-6 text-sm text-muted-foreground space-y-3">
                    <h2 className="text-base font-semibold text-foreground">How Staking Works on TokenKickstarter</h2>
                    <p>
                        Staking lets you earn passive rewards by locking your tokens for a set period. The longer
                        you stake, the higher your annual percentage yield (APY). All staking contracts are
                        on-chain and fully audited.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>TKS staking</strong> — stake native TKS tokens across multiple duration tiers.</li>
                        <li><strong>LP staking</strong> — stake liquidity pool tokens from supported DEXs.</li>
                        <li><strong>Stablecoin pools</strong> — earn yield on USDT, USDC, and other stablecoins.</li>
                        <li><strong>Flexible durations</strong> — choose from 30, 90, 180, or 365 day lock periods.</li>
                    </ul>
                    <p>
                        Rewards are calculated in real time and displayed on your dashboard. You can claim
                        earned rewards at any time without unstaking your principal. When the lock period ends,
                        withdraw your full deposit with all accumulated rewards.
                    </p>
                </div>
            </section>

            <Footer />
        </div>
    )
}
